import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveInsight } from '../../store/slices/layoutSlice';
import { getViewTypeFromInsight } from '../../utils/insights';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid2 as Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

function CodeBlock({ children }: { children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundColor: 'tertiary.light',
        border: '1px solid',
        borderColor: 'tertiary.main',
        borderRadius: 1,
        fontFamily: 'Menlo, monospace',
        paddingX: 0.5,
      }}
      component='span'
    >
      {children}
    </Box>
  );
}

export default function ViewInfoPanel() {
  const activeView = useSelector(selectActiveInsight);
  const [copyTitle, setCopyTitle] = useState('Copy as JSON');
  const [showFilters, setShowFilters] = useState(false);

  const onCopy = useCallback(() => {
    setCopyTitle('Copied!');
    navigator.clipboard.writeText(JSON.stringify(activeView));
  }, [activeView]);

  if (!activeView) {
    return <Box sx={{ width: 600, p: 2 }}>No active report!</Box>;
  }

  const viewType = getViewTypeFromInsight(activeView);

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid size={8}>
          <Typography variant='subtitle2'>View type</Typography>
          <CodeBlock>{viewType}</CodeBlock>
        </Grid>
        <Grid size={4}>
          <Button
            fullWidth
            variant='contained'
            color='secondary'
            onClick={onCopy}
          >
            {copyTitle}
          </Button>
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid size={4}>
          <Typography variant='subtitle2'>Data type</Typography>
          <CodeBlock>{activeView.insightType}</CodeBlock>
        </Grid>
        <Grid size={4}>
          <Typography variant='subtitle2'>Grouping</Typography>
          <CodeBlock>{activeView.groupBy ?? 'null'}</CodeBlock>
        </Grid>
        <Grid size={4}>
          <Typography variant='subtitle2'>Public</Typography>
          <CodeBlock>{activeView.public ? 'true' : 'false'}</CodeBlock>
        </Grid>
        <Grid size={4}>
          <Typography variant='subtitle2'>Expanded</Typography>
          <CodeBlock>{activeView.expanded ? 'true' : 'false'}</CodeBlock>
        </Grid>
        <Grid size={8}>
          <Typography variant='subtitle2'>Creator</Typography>
          {activeView.creatorName}
          {!activeView.creatorId && ' (autogenerated)'}
        </Grid>
        {activeView.dateRanges?.map((dateRange, i) => (
          <React.Fragment key={i}>
            <Grid size={4}>
              <Typography variant='subtitle2'>
                Date range {i + 1} type
              </Typography>
              <CodeBlock>{dateRange.dateRangeType ?? 'null'}</CodeBlock>
            </Grid>
            <Grid size={4}>
              <Typography variant='subtitle2'>
                Date range {i + 1} start
              </Typography>
              <CodeBlock>{dateRange.startDate ?? 'null'}</CodeBlock>
            </Grid>
            <Grid size={4}>
              <Typography variant='subtitle2'>
                Date range {i + 1} end
              </Typography>
              <CodeBlock>{dateRange.endDate ?? 'null'}</CodeBlock>
            </Grid>
            <Grid size={12}>
              <Typography variant='subtitle2'>
                Date range {i + 1} days
              </Typography>
              <CodeBlock>{`[${dateRange.days?.join(', ')}]`}</CodeBlock>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
      <Divider />
      {(activeView.filters?.length ?? 0) > 0 && (
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='subtitle2'>Filters</Typography>
            <IconButton
              size='small'
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={showFilters}>
            <Box>
              {activeView.filters?.map((filter, i) => (
                <Grid
                  key={i}
                  container
                  spacing={2}
                  sx={{
                    border: '1px solid',
                    borderColor: 'tertiary.main',
                    borderRadius: 2,
                    p: 2,
                    mb: 1,
                  }}
                >
                  <Grid size={3}>
                    <Typography variant='subtitle2'>Type</Typography>
                    <CodeBlock>{filter.type}</CodeBlock>
                  </Grid>
                  {'comparison' in filter && (
                    <Grid size={3}>
                      <Typography variant='subtitle2'>Comparison</Typography>
                      <CodeBlock>{filter.comparison}</CodeBlock>
                    </Grid>
                  )}
                  {'kpi' in filter && (
                    <Grid size={3}>
                      <Typography variant='subtitle2'>KPI</Typography>
                      <CodeBlock>{filter.kpi}</CodeBlock>
                    </Grid>
                  )}
                  {'value' in filter && (
                    <Grid size={3}>
                      <Typography variant='subtitle2'>Value</Typography>
                      <CodeBlock>{filter.value}</CodeBlock>
                    </Grid>
                  )}
                  {'filterIds' in filter && (
                    <Grid size={12}>
                      <Typography variant='subtitle2'>Filter IDs</Typography>
                      <CodeBlock>{`[${filter.filterIds.join(', ')}]`}</CodeBlock>
                    </Grid>
                  )}
                </Grid>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
